/**
 * Minimal frontend ABI for the calls we make against an Ajna ERC20 pool.
 * The full pool surface lives in `ajna-finance/ajna-core`; only the reads
 * needed for the dapp are reproduced here.
 */
export const AJNA_POOL_ABI = [
  {
    type: "function",
    name: "borrowerInfo",
    stateMutability: "view",
    inputs: [{ name: "borrower", type: "address" }],
    outputs: [
      // t0debt: borrower's debt scaled by the inflator at time-0.
      // Current debt = t0debt * inflator / 1e18.
      { name: "t0debt", type: "uint256" },
      // Collateral pledged, 1e18 scale.
      { name: "collateral", type: "uint256" },
      // Borrower-specific neutral-price / threshold-price ratio (1e18).
      { name: "npTpRatio", type: "uint256" },
    ],
  },
  {
    // Pool accounting; first two slots are what we need for debt scaling.
    type: "function",
    name: "inflatorInfo",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "inflator", type: "uint256" },
      { name: "lastUpdated", type: "uint256" },
    ],
  },
  {
    // Total quote-token (USDC) deposited into the pool, 1e18 scale.
    type: "function",
    name: "depositSize",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "debtInfo",
    stateMutability: "view",
    inputs: [],
    outputs: [
      // Total pool debt, 1e18 scale.
      { name: "debt_", type: "uint256" },
      // Accrual debt (t0debt projected).
      { name: "accrualDebt_", type: "uint256" },
      // Debt in auction.
      { name: "debtInAuction_", type: "uint256" },
      // T0 debt placeholder.
      { name: "t0Debt2ToCollateral_", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "interestRateInfo",
    stateMutability: "view",
    inputs: [],
    outputs: [
      // Interest rate, 1e18 scale (per-year continuous compounding base).
      { name: "interestRate", type: "uint256" },
      { name: "interestRateUpdate", type: "uint256" },
    ],
  },
  {
    // Pool pricing snapshot. The 5th return slot is the Lowest Utilised Price
    // (LUP) at 1e18 scale — quote-per-collateral. Used for liquidation math.
    type: "function",
    name: "pricesInfo",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "hpb", type: "uint256" },
      { name: "hpbIndex", type: "uint256" },
      { name: "htp", type: "uint256" },
      { name: "htpIndex", type: "uint256" },
      { name: "lup", type: "uint256" },
      { name: "lupIndex", type: "uint256" },
    ],
  },
  {
    // Supply quote tokens into a price bucket. index is the bucket index;
    // expiry is a Unix timestamp hard deadline (reverts if block.timestamp > expiry).
    type: "function",
    name: "addQuoteToken",
    stateMutability: "nonpayable",
    inputs: [
      { name: "amount", type: "uint256" },
      { name: "index", type: "uint256" },
      { name: "expiry", type: "uint256" },
    ],
    outputs: [
      { name: "bucketLP", type: "uint256" },
      { name: "addedAmount", type: "uint256" },
    ],
  },
  {
    // Remove quote tokens from a bucket. Pass maxAmount = uint256.max to remove all.
    type: "function",
    name: "removeQuoteToken",
    stateMutability: "nonpayable",
    inputs: [
      { name: "maxAmount", type: "uint256" },
      { name: "index", type: "uint256" },
    ],
    outputs: [
      { name: "removedAmount", type: "uint256" },
      { name: "redeemedLP", type: "uint256" },
    ],
  },
  {
    // LP balance and deposit time for a lender at a given bucket index.
    type: "function",
    name: "lenderInfo",
    stateMutability: "view",
    inputs: [
      { name: "index", type: "uint256" },
      { name: "lender", type: "address" },
    ],
    outputs: [
      { name: "lpBalance", type: "uint256" },
      { name: "depositTime", type: "uint256" },
    ],
  },
  {
    // Convert LP balance at a bucket to its equivalent quote-token value.
    type: "function",
    name: "lpToQuoteTokens",
    stateMutability: "view",
    inputs: [
      { name: "lp", type: "uint256" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ name: "quoteAmount_", type: "uint256" }],
  },
] as const;
